import React from "react"

import {
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField, Tooltip,
	Typography,
} from "@mui/material"
import {TabContext,} from "@mui/lab"

import {t, Trans,} from "@lingui/macro"
import {Editor} from "react-draft-wysiwyg"
import {convertFromRaw, convertToRaw, EditorState,} from "draft-js"
import Icon from "@mdi/react"
import {mdiAccountOutline, mdiCheckboxMarkedCircleOutline, mdiClockTimeEightOutline, mdiSkipNext,} from "@mdi/js"

import {Action, ActionStatus, ActionUtils, UpdateActionRequest, WorkflowStepType,} from "models"
import {SbTab, SbTabList,} from "components/commons"

interface WorkflowColumnThreeProps
{
	action: Action
	openAssignWorkflowActionDialog: (action: Action) => void
	openDeferActionDialog: (action: Action) => void
	openSkipDialog: (actionId: string) => void
	markActionAsDone: (action: Action) => void
	updateActionContent: (action: Action, request: UpdateActionRequest) => void
}
interface WorkflowColumnThreeState
{
	editorState: EditorState
	actionName: string
	actionType: WorkflowStepType
	loading: boolean
}
export class WorkflowColumnThree extends React.Component<WorkflowColumnThreeProps, WorkflowColumnThreeState>
{
	constructor(props: WorkflowColumnThreeProps)
	{
		super(props)
		this.state = {
			editorState: EditorState.createEmpty(),
			actionName: "",
			actionType: WorkflowStepType.NOTIFICATION,
			loading: true
		}
	}

	public componentDidMount()
	{
		this.initForm()
	}

	public componentDidUpdate(
		prevProps: Readonly<WorkflowColumnThreeProps>,
		prevState: Readonly<WorkflowColumnThreeState>,
		snapshot?: any,
	)
	{
		if(prevProps.action.id === this.props.action.id) {
			return
		}

		this.initForm()
	}

	private initForm = (): void => {
		if(this.props.action){
			const contentState = convertFromRaw(this.props.action.content)
			this.setState(() => ({
				editorState: EditorState.createWithContent(contentState),
				actionName: this.props.action.name,
				actionType: this.props.action.actionType,
				loading: false
			}))
		}
	}

	private handleEditorChange = (editorState: any) => {
		this.setState(() => ({
			editorState: editorState,
		}))
	}

	private handleNameChange = (event: any) => {
		this.setState(() => ({
			actionName: event.target.value
		}))
	}

	private handleActionTypeChange = (event: any) => {
		const newValue = ActionUtils.renderObject(event.target.value)

		if(newValue){
			this.setState(() => ({
				actionType: newValue
			}))
		}
	}

	private updateActionContent = (): void => {
		let request: UpdateActionRequest = {
			content: convertToRaw(this.state.editorState.getCurrentContent()),
			name: this.state.actionName,
			actionType: this.state.actionType
		}

		this.props.updateActionContent(
			this.props.action,
			request
		)
	}

	render()
	{
		let selectedActionString = ActionUtils.renderString(this.state.actionType)

		return (
			<>
				{
					!this.state.loading &&
					<>
						<TabContext value="1">
							<Box sx={{ borderBottom: 0 }}>
								<SbTabList variant="fullWidth">
									<SbTab
										disableRipple
										disableTouchRipple
										label={t({ id: "workflow.details", message: "Dettagli azione", })}
										value="1" />
								</SbTabList>
							</Box>
						</TabContext>
						<Box sx={{mr: 3}}>
							<Grid container overflow="auto" rowSpacing={3}>
								<Grid item xs={12} sx={{mt: 3}}>
									<TextField variant="outlined"
											   label={t({
													id: "actions.second-column.action-name",
													message: "Nome azione",
											   })}
											   name="actionName"
											   value={this.state.actionName}
											   onChange={this.handleNameChange}
									/>
								</Grid>
								<Grid item xs={12}>
									<Editor
										readOnly={this.props.action.status !== ActionStatus.WORKING && this.props.action.status !== ActionStatus.PENDING}
										editorState={this.state.editorState}
										onEditorStateChange={this.handleEditorChange}
										toolbar={{
											options: ['inline', 'list', 'link'],
											inline: {
												options: ['bold', 'italic'],
											},

											list: {
												options: ['unordered', 'ordered'],
											},
											link: {
												options: ['link'],
											},
										}}
										toolbarStyle={{
											backgroundColor: "#11347B",
											borderColor: "#11347B"
										}}
										editorStyle={{
											minHeight: "32vh",
											border: "1px solid lightgray",
										}}
									/>
								</Grid>
							</Grid>
							<Grid container sx={{mt: 3}}>
								<Grid item xs={12} md={5}>
									<FormControl>
										<InputLabel id="actionTypeLabel">{t({id: "workflow.column-three.type-label", message: "Tipo di azione"})}</InputLabel>
										<Select variant="outlined"
												labelId="actionTypeLabel"
												label={t({id: "workflow.column-three.type-label", message: "Tipo di azione"})}
												name="actionType"
												onChange={this.handleActionTypeChange}
												value={selectedActionString}
												sx={{width: "200px"}}
										>
											{Object.keys(WorkflowStepType).map((typeKey, index) =>
												<MenuItem value={typeKey} key={typeKey}>
													<Trans id={ `enums.workflow-step-types.${typeKey}`}>

													</Trans>
												</MenuItem>
											)}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} md={7}>
									<Box width="100%" textAlign="right">
										{(this.props.action.status !== ActionStatus.DONE && this.props.action.status !== ActionStatus.SKIPPED) &&
											<>
												<Tooltip title="Assegna" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="assign"
														onClick={() => this.props.openAssignWorkflowActionDialog(this.props.action)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiAccountOutline} size={1} />
													</IconButton>
												</Tooltip>
												<Tooltip title="Posticipa" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="postpone"
														onClick={() => this.props.openDeferActionDialog(this.props.action)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiClockTimeEightOutline} size={1} />
													</IconButton>
												</Tooltip>
											</>
										}
										{this.props.action.status === ActionStatus.WORKING &&
											<>
												<Tooltip title="Salta" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="skip"
														onClick={() => this.props.openSkipDialog(this.props.action.id)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiSkipNext} size={1} />
													</IconButton>
												</Tooltip>
												<Tooltip title="Eseguita" placement="top" arrow>
													<IconButton
														sx={{
															color: 'grey',
															backgroundColor: "transparent",
															":hover": {
																color: "secondary.main"
															}
														}}
														aria-label="Delete"
														onClick={() => this.props.markActionAsDone(this.props.action)}
														disableFocusRipple
														disableRipple>
														<Icon path={mdiCheckboxMarkedCircleOutline} size={1} />
													</IconButton>
												</Tooltip>
											</>
										}
										{(this.props.action.status !== ActionStatus.DONE && this.props.action.status !== ActionStatus.SKIPPED) &&
											<Button variant="contained"
													type="button"
													disableElevation
													sx={{
														textTransform: "none",
														borderColor: "primary.main",
														bgcolor: "primary.main",
														paddingY: 0,
														paddingX: 2
													}}
													onClick={this.updateActionContent}
											>
												<Typography>
													<Trans id="actions.save">
														Salva
													</Trans>
												</Typography>
											</Button>
										}
									</Box>
								</Grid>
							</Grid>
						</Box>
					</>
				}
			</>
		)
	}
}
